import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Copy text to the clipboard and flash a transient "copied" flag for `resetMs`.
 *
 * Tracks the pending reset timeout in a ref and clears it on unmount so the
 * setState that flips `copied` back to `false` never fires after the component
 * has been unmounted (see F24-001).
 */
export function useCopyToast(resetMs = 2000): {
  copied: boolean;
  copy: (text: string | null | undefined) => Promise<void>;
  reset: () => void;
} {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPending = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  useEffect(() => clearPending, [clearPending]);

  const copy = useCallback(
    async (text: string | null | undefined) => {
      if (!text) return;
      await navigator.clipboard.writeText(text);
      setCopied(true);
      clearPending();
      timeoutRef.current = setTimeout(() => setCopied(false), resetMs);
    },
    [resetMs, clearPending]
  );

  const reset = useCallback(() => {
    clearPending();
    setCopied(false);
  }, [clearPending]);

  return { copied, copy, reset };
}
